import { apiClient } from "@/api/api-client";
import CaseActivities from "@/components/cases/case-activities";
import { Button } from "@/components/core/buttons/button";
import { useModal } from "@/components/core/modals/use-modal";
import { Searchbar } from "@/components/core/searchbar";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@/components/core/tabs";
import { Content } from "@/components/layouts/content";
import { caseStages } from "@/constants/case-stages";
import activities from "@/data/case-activity";
import DashboardEvents from "@/features/case-classification/dashboard-events";
import IncomingCases from "@/features/case-classification/incoming-cases";
import IncomingJdr from "@/features/case-classification/incoming-jdr";
import IncomingPayments from "@/features/case-classification/incoming-payments";
import CaseTopCategory from "@/features/case-evaluation/components/case-top-category";
import { caseActivityPaymentQueryOptions } from "@/features/case-record/api/queries/case-activitiy-payment-query";
import CaseSearchModal from "@/features/case-record/components/case-search-modal";
import CaseOccStats from "@/features/overview/components/case-occ-stats";
import CaseStageChart from "@/features/overview/components/case-stage-chart";
import StatCard from "@/features/overview/components/stat-card";
import { useAuth } from "@/hooks/use-auth";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import data from "intl-tel-input/data";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/_dashboard/occ-dashboard")({
	loader: ({ context: { queryClient }, deps }) => {
		return queryClient.ensureQueryData(
			caseActivityPaymentQueryOptions({
				...deps,
				status: "pending",
			}),
		);
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { user } = useAuth();
	const search = Route.useSearch();
	const navigate = useNavigate({
		from: "/occ-dashboard",
	});
	const modal = useModal();
	const [totalCases, setTotalCases] = useState(0);
	const [totalMotions, setTotalMotions] = useState(0);
	const [totalJdr, setTotalJdr] = useState(0);
	const [breakdown, setBreakdown] = useState(0);

	const fetchCaseCount = async () => {
		const response = await apiClient.get("/backoffice/case-counts");
		setTotalCases(response.data.data?.total_cases);
		setTotalMotions(response.data.data?.total_motions);
		setTotalJdr(response.data.data?.total_jrd);
	};

	const fetchCaseRecordBreakdown = async () => {
		const response = await apiClient.get(
			"/backoffice/case-records-breakdown-branch",
		);
		setBreakdown(response.data);
	};

	useEffect(() => {
		fetchCaseRecordBreakdown();
	}, []);

	useEffect(() => {
		fetchCaseCount();
	}, []);

	const openSearchModal = (value) => {
		modal.openModal({
			allowClose: true,
			size: "lg",
			title: `Result for ${value}`,
			showHeader: true,
			component: <CaseSearchModal />,
		});
	};

	return (
		<Content>
			<div className="flex flex-col w-full max-w-base p-6 gap-4">
				<div className="flex flex-col w-full">
					<span className="text-xl text-foreground-dark font-semibold">
						Welcome {user?.first_name},
					</span>
					<span className="text-base text-foreground/70">
						View assigned cases and updates
					</span>
				</div>
				<div className="flex flex-col w-full gap-4">
					<div className="grid grid-cols-12 w-full gap-4">
						<div className="col-span-12">
							<div className="flex flex-col w-full h-full rounded-md border border-border/50 overflow-hidden">
								<div className="flex flex-row w-full p-3 border-b border-border/50">
									<span className="text-base font-semibold text-foreground-dark">
										Total Cases Per Branch
									</span>
								</div>
								<div className="flex flex-col w-full">
									{breakdown && <CaseOccStats items={breakdown} />}
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="flex flex-col w-full h-full rounded-md border border-border/50 overflow-hidden">
					<div className="flex flex-row w-full p-3 border-b border-border/50">
						<span className="text-base font-semibold text-foreground-dark">
							Incoming Cases / Raffle
						</span>
					</div>
					<div className="flex flex-col w-full">
						<Tabs
							defaultValue={search.tabs || "cases"}
							onValueChange={async (value) => {
								await navigate({
									to: "/occ-dashboard",
									search: (old) => ({
										...old,
										tabs: value,
									}),
								});
							}}
						>
							<TabsList>
								<TabsTrigger
									value="cases"
									count={totalCases ? totalCases : null}
								>
									Incoming/ Filed Cases
								</TabsTrigger>
								<TabsTrigger
									value="motions"
									count={totalMotions ? totalMotions : null}
								>
									Incoming Payments
								</TabsTrigger>
								<TabsTrigger value="jdr" count={totalJdr ? totalJdr : null}>
									JDR Case
								</TabsTrigger>
							</TabsList>
							<TabsContent value="cases">
								<IncomingCases />
							</TabsContent>
							<TabsContent value="motions">
								<IncomingPayments />
							</TabsContent>
							<TabsContent value="jdr">
								<IncomingJdr />
							</TabsContent>
						</Tabs>
					</div>
				</div>
			</div>
		</Content>
	);
}
